import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Pencil, Trash2, Wallet, Landmark } from "lucide-react";

const fmt = (n) => `${(Number(n) || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const num = (v) => Number(String(v ?? "").replace(",", ".")) || 0;
const r2 = (n) => Math.round(n * 100) / 100;
const METODI = [["bonifico", "Bonifico"], ["pos", "POS"], ["contanti", "Contanti"], ["assegno", "Assegno"], ["altro", "Altro"]];
const oggi = () => new Date().toISOString().slice(0, 10);
const vuoto = () => ({ id: null, importo: "", data_pagamento: oggi(), mezzo_pagamento: "bonifico", note: "", parlante: false, accreditato: "" });

const Riepilogo = ({ d }) => (
  <div className="grid grid-cols-3 gap-2 text-center" data-testid="pag-riepilogo">
    {[["Totale", d.totale, ""], ["Già pagato", d.importo_pagato, "text-emerald-700"], ["Residuo", d.saldo_residuo, "text-primary"]].map(([l, v, c]) => (
      <div key={l} className="rounded-md bg-secondary p-2">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{l}</div>
        <div className={`font-extrabold tabular-nums ${c}`} data-testid={`pag-${l.toLowerCase().replace(/\W+/g, "-")}`}>{fmt(v)}</div>
      </div>
    ))}
  </div>
);

const Ritenuta = ({ form, setForm, pct }) => {
  const imp = num(form.importo);
  const rit = r2(imp / 1.22 * pct / 100);
  const fromNetto = (v) => {
    const lordo = r2(num(v) / (1 - pct / 100 / 1.22));
    setForm({ ...form, accreditato: v, importo: v === "" ? "" : String(lordo) });
  };
  return (
    <div className="rounded-md border border-primary/40 bg-primary/5 p-3 space-y-2 text-sm" data-testid="pag-ritenuta-box">
      <div className="flex justify-between"><span>Ritenuta {pct}% su imponibile ({fmt(imp / 1.22)})</span><strong className="tabular-nums" data-testid="pag-ritenuta">{fmt(rit)}</strong></div>
      <div className="flex justify-between"><span>Accreditato in conto</span><strong className="tabular-nums text-emerald-700" data-testid="pag-accreditato">{fmt(imp - rit)}</strong></div>
      <div>
        <Label className="text-xs">Oppure parti dalla somma arrivata in banca</Label>
        <Input data-testid="pag-netto" type="number" inputMode="decimal" step="0.01" value={form.accreditato} onChange={(e) => fromNetto(e.target.value)} className="h-9" placeholder="es. 6368,85" />
      </div>
    </div>
  );
};

const ListaPagamenti = ({ pags, onEdit, onDelete }) => pags.length > 0 && (
  <div className="space-y-1" data-testid="pag-lista">
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Pagamenti registrati</div>
    {pags.map((p) => (
      <div key={p.id} className="flex items-center justify-between gap-2 rounded border border-border px-2 py-1.5 text-xs" data-testid={`pag-row-${p.id}`}>
        <div>
          <div className="font-semibold tabular-nums">{fmt(p.importo)} · {p.data_pagamento} · {p.mezzo_pagamento || "—"}</div>
          {p.parlante && <div className="text-primary">Bonifico parlante · ritenuta {fmt(p.ritenuta)} · accreditati {fmt(p.accreditato)}</div>}
        </div>
        <div className="flex">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(p)} data-testid={`pag-edit-${p.id}`}><Pencil className="h-3 w-3" /></Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onDelete(p)} data-testid={`pag-del-${p.id}`}><Trash2 className="h-3 w-3 text-destructive" /></Button>
        </div>
      </div>
    ))}
  </div>
);

export const PagamentoFattura = ({ fid, onClose, onSaved }) => {
  const [d, setD] = useState(null);
  const [form, setForm] = useState(vuoto());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!fid) return;
    setD(null); setForm(vuoto());
    api.get(`/fatture/${fid}/pagamenti-dettaglio`).then(({ data }) => setD(data)).catch(() => toast.error("Errore caricamento fattura"));
  }, [fid]);

  const editing = form.id ? d?.pagamenti.find((p) => p.id === form.id) : null;
  const residuo = r2((d?.saldo_residuo || 0) + (editing ? Number(editing.importo) : 0));
  const quick = (pct) => setForm({ ...form, accreditato: "", importo: String(pct ? Math.min(residuo, r2(d.totale * pct / 100)) : residuo) });
  const vendita = d?.tipo === "vendita";

  const done = (data, msg) => { setD({ ...d, ...data }); setForm(vuoto()); toast.success(msg); onSaved?.(); };
  const save = async () => {
    if (num(form.importo) <= 0) return toast.error("Inserisci l'importo pagato");
    setBusy(true);
    const body = { importo: num(form.importo), data_pagamento: form.data_pagamento, mezzo_pagamento: form.mezzo_pagamento, note: form.note, parlante: vendita && form.parlante };
    try {
      const { data } = form.id ? await api.put(`/fatture/${fid}/pagamenti/${form.id}`, body) : await api.post(`/fatture/${fid}/pagamenti`, body);
      done(data, form.id ? "Pagamento aggiornato" : "Pagamento registrato");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };
  const del = async (p) => {
    if (!window.confirm(`Eliminare il pagamento di ${fmt(p.importo)}?`)) return;
    try { const { data } = await api.delete(`/fatture/${fid}/pagamenti/${p.id}`); done(data, "Pagamento eliminato"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const edit = (p) => setForm({ id: p.id, importo: String(p.importo), data_pagamento: p.data_pagamento || oggi(), mezzo_pagamento: p.mezzo_pagamento || "bonifico", note: p.note || "", parlante: !!p.parlante, accreditato: "" });

  return (
    <Dialog open={!!fid} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="pag-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wallet className="h-5 w-5 text-primary" /> Registra pagamento</DialogTitle>
          <DialogDescription>{d ? `Fattura n. ${d.numero || "—"} · ${d.controparte || ""}` : "Caricamento…"}</DialogDescription>
        </DialogHeader>
        {!d ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : (
          <div className="space-y-4">
            <Riepilogo d={d} />
            <ListaPagamenti pags={d.pagamenti || []} onEdit={edit} onDelete={del} />
            {(residuo > 0 || form.id) ? (
              <div className="space-y-3">
                <div className="text-sm font-semibold">{form.id ? "Modifica pagamento" : "Nuovo pagamento"}</div>
                <div className="grid grid-cols-4 gap-2">
                  {[20, 50, 80].map((p) => <Button key={p} type="button" variant="outline" className="h-12 text-base font-bold" onClick={() => quick(p)} data-testid={`pag-pct-${p}`}>{p}%</Button>)}
                  <Button type="button" variant="outline" className="h-12 text-base font-bold" onClick={() => quick(0)} data-testid="pag-saldo">Saldo</Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>{form.parlante ? "Importo del bonifico €" : "Importo pagato €"}</Label><Input data-testid="pag-importo" type="number" inputMode="decimal" step="0.01" value={form.importo} onChange={(e) => setForm({ ...form, importo: e.target.value, accreditato: "" })} className="h-11 text-lg font-bold" /></div>
                  <div><Label>Data</Label><Input data-testid="pag-data" type="date" value={form.data_pagamento} onChange={(e) => setForm({ ...form, data_pagamento: e.target.value })} className="h-11" /></div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Metodo</Label>
                    <select data-testid="pag-metodo" value={form.mezzo_pagamento} onChange={(e) => setForm({ ...form, mezzo_pagamento: e.target.value, parlante: e.target.value === "bonifico" && form.parlante })} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                      {METODI.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <div><Label>Note</Label><Input data-testid="pag-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="h-10" /></div>
                </div>
                {vendita && form.mezzo_pagamento === "bonifico" && (
                  <label className="flex items-center justify-between gap-2 rounded-md border border-border p-3 cursor-pointer">
                    <span className="flex items-center gap-2 text-sm"><Landmark className="h-4 w-4 text-primary" /> Bonifico parlante (detrazione fiscale)</span>
                    <Switch data-testid="pag-parlante" checked={form.parlante} onCheckedChange={(v) => setForm({ ...form, parlante: v, accreditato: "" })} />
                  </label>
                )}
                {vendita && form.parlante && <Ritenuta form={form} setForm={setForm} pct={d.ritenuta_pct} />}
                {num(form.importo) > residuo + 0.005 && <div className="text-xs text-destructive" data-testid="pag-eccedenza">L'importo supera il residuo di {fmt(residuo)}.</div>}
                <div className="flex gap-2">
                  {form.id && <Button variant="outline" className="h-11" onClick={() => setForm(vuoto())}>Annulla</Button>}
                  <Button data-testid="pag-salva" onClick={save} disabled={busy || num(form.importo) > residuo + 0.005} className="flex-1 h-11">
                    {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{form.id ? "Salva modifica" : `Registra ${fmt(num(form.importo))}`}
                  </Button>
                </div>
              </div>
            ) : <div className="text-sm text-emerald-700 font-semibold text-center" data-testid="pag-saldata">Fattura saldata.</div>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
